import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ValidationErrors, Validators } from '@angular/forms';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { OdometerReading } from 'src/app/entities/OdometerReading';
import { ClockOdometerValueFormControls } from 'src/app/models/form-groups/clock-odometer-value-form.model';
import { OdometerReadingService } from 'src/app/services/odometer-reading.service';
import { TimeService } from 'src/app/services/time.service';
import { DateTimeHelper, DateTimeType } from 'src/app/utils/date-time-helper';
import { ImageSelectorUiComponent, ImagePickerPhotoVm } from '../../shared/common/image-selector-ui/image-selector-ui.component';
import { MediaSource, MediaSourceSelectionService } from 'src/app/services/media-source-selection.service';
import { PhotoService } from 'src/app/services/photo.service';
import { Guid } from 'src/app/constant/guid';
import { GlobalConstants } from 'src/app/constant/constant';
import { CameraSource } from '@capacitor/camera';
import { LogService } from 'src/app/baseServices/logging/log.service';
import { PendingPhoto } from 'src/app/models/photo/pending-photo';


export interface ClockOdometerControlsValues {
  trackMileage: boolean | null;
  odometerValue: number | null;
  odometerPhoto: PendingPhoto | null;
}
export interface ClockOdometerDataValues extends ClockOdometerControlsValues {
  isValid: boolean;
}

@Component({
  selector: 'app-clock-odometer-value',
  templateUrl: './clock-odometer-value.component.html',
  styleUrls: ['./clock-odometer-value.component.scss'],
})
export class ClockOdometerValueComponent implements OnInit, OnChanges, OnDestroy {
  @Input() userId: number;
  @Input() date: DateTimeType;
  /** ProjectId the user is currently clocked into, or null if not clocked into any project. */
  @Input() currentProjectId: number | null;
  /** ProjectId the user is transitioning to, or null if transitioning to no project (clocked out). */
  @Input() nextProjectId: number | null;
  @ViewChild(ImageSelectorUiComponent) imagePickerUi: ImageSelectorUiComponent;

  private destroy$ = new Subject<void>();

  @Output() valueChanged = new EventEmitter<ClockOdometerDataValues>();
  isLoading = true;
  showTrackMileageInput = false;
  showOdometerValueInput = false;
  openOdometerReading: OdometerReading;
  formGroup: FormGroup<ClockOdometerValueFormControls>;

  odometerPhotoVm: ImagePickerPhotoVm<PendingPhoto> | null = null;
  isCapturingPhoto = false;


  sliderOptions = {
    centeredSlides: false,
    autoHeight: false,
    loop: false,
    slidesPerView: 2.4,
    coverflowEffect: {
      rotate: 50,
      stretch: 0,
      depth: 100,
      modifier: 1,
      slideShadows: true,
    },
    allowTouchMove: false,
    simulateTouch: false
  };

  constructor(
    private odometerReadingService: OdometerReadingService,
    private timeService: TimeService,
    private photoService: PhotoService,
    private mediaSourceSelectionService: MediaSourceSelectionService,
    private logService: LogService
  ) { }
  async ngOnInit() {
    this.initializeFormGroup();
    await this.updateFormState();
  }

  async ngOnChanges(changes: SimpleChanges) {
    if (!this.formGroup) {
      return;
    }
    this.formGroup.reset();
    await this.updateFormState();
  }

  private async updateFormState() {
    this.isLoading = true;
    await this.loadData();
    await this.updateControlVisibility();
    this.formGroup.updateValueAndValidity({ emitEvent: false });
    this.emitFormChanged();
    this.isLoading = false;
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initializeFormGroup() {
    const trackMileageControl = new FormControl(null, {
      validators: [this.requiredIfVisible(() => this.showTrackMileageInput)]
    });

    const odometerControl = new FormControl<number | null>(null, {
      validators: [
        Validators.min(0),
        this.requiredIfVisible(() => this.showOdometerValueInput),
        this.validateOdometerRange()
      ],
      asyncValidators: [
        this.validateOdometerReadingHasNoOverlap.bind(this)
      ],
      updateOn: 'change'
    });
    const odometerPhotoControl = new FormControl<PendingPhoto | null>(null);


    this.formGroup = new FormGroup<ClockOdometerValueFormControls>({
      trackMileage: trackMileageControl,
      odometerValue: odometerControl,
      odometerPhoto: odometerPhotoControl
    });

    this.formGroup.statusChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.emitFormChanged());
  }

  async onTrackMileageChange(event: any) {
    await this.updateOdometerValueInputVisibility();

    if (this.formGroup.value.trackMileage !== true) {
      this.clearOdometerPhoto(false);
    }

    this.emitFormChanged();
  }

  async onOdometerValueChange(event: any) {
    this.emitFormChanged();
  }

  private emitFormChanged() {
    this.valueChanged.emit({
      trackMileage: this.formGroup.value.trackMileage,
      odometerValue: this.formGroup.value.odometerValue,
      odometerPhoto: this.formGroup.value.odometerPhoto,
      isValid: this.formGroup.valid,
    });
  }
  onOdometerPhotoRemoveRequested(photoVm: ImagePickerPhotoVm<PendingPhoto>, event: Event) {
    event.stopPropagation();
    this.clearOdometerPhoto(true);

    void this.imagePickerUi?.refreshSlides();
  }

  async onOdometerPhotoCaptureRequested() {
    if (this.isCapturingPhoto) {
      return; // Prevent concurrent capture operations
    }

    const selectedSource = await this.mediaSourceSelectionService.promptForSelection({
      header: this.odometerPhotoVm ? 'Retake Photo' : 'Add Photo',
      cameraText: 'Take Photo',
      galleryText: 'Select from Gallery',
    });

    if (!selectedSource) return;

    const cameraSource = selectedSource === MediaSource.Camera ? CameraSource.Camera : CameraSource.Photos;

    this.isCapturingPhoto = true;
    
    try {
      const captureResult = await this.photoService.addNewToGallery(
        cameraSource,
        { width: GlobalConstants.DEFAULT_CANVAS_WIDTH, height: GlobalConstants.DEFAULT_CANVAS_HEIGHT },
        undefined
      );

      if (!captureResult?.capturedPhoto?.dataUrl) {
        this.logService.error('OdometerPhoto.CaptureFailedNoData', 'Photo capture failed: No data URL available');
        return;
      }

      const newPhoto: PendingPhoto = {
        dataUrl: captureResult.capturedPhoto.dataUrl,
        format: captureResult.capturedPhoto.format,
        fileName: 'File' + Guid.newGuid() + '.' + captureResult.capturedPhoto.format,
        title: 'Odometer Photo',
      };

      this.formGroup.controls.odometerPhoto.setValue(newPhoto);
      this.formGroup.controls.odometerPhoto.markAsDirty();

      this.odometerPhotoVm = {
        meta: newPhoto,
        title: newPhoto.title ?? 'Odometer Photo',
        fileName: newPhoto.fileName,
        src: captureResult.capturedPhoto.dataUrl,
      };

      this.emitFormChanged();
    } catch (error) {
      this.logService.error('OdometerPhoto.CaptureError', error, { userId: this.userId, projectId: this.nextProjectId });
    } finally {
      this.isCapturingPhoto = false;
    }
  }
  private clearOdometerPhoto(emit: boolean) {
    this.formGroup.controls.odometerPhoto.setValue(null, { emitEvent: false });
    this.formGroup.controls.odometerPhoto.markAsDirty();
    this.odometerPhotoVm = null;

    if (emit) {
      this.emitFormChanged();
    }
  }


  private requiredIfVisible(isVisible: () => boolean) {
    return (control: AbstractControl) => {
      if (!isVisible()) {
        return null;
      }
      return Validators.required(control);
    };
  }

  private async loadData() {
    await this.loadOpenOdometerReading();
  }

  private async loadOpenOdometerReading() {
    this.openOdometerReading = await this.odometerReadingService.getOpenOdometerReading(this.userId, this.date);
  }

  private async updateControlVisibility() {
    await this.updateTrackMileageInputVisibility();
    await this.updateOdometerValueInputVisibility();
  }

  private async updateTrackMileageInputVisibility() {
    const wasVisible = this.showTrackMileageInput;

    if (this.isChangingProjects && this.nextProjectId !== null) {
      this.showTrackMileageInput = await this.timeService.checkProjectAllowsMileageTracking(this.nextProjectId);
    } else {
      this.showTrackMileageInput = false;
    }

    if (wasVisible !== this.showTrackMileageInput) {
      const control = this.formGroup.get('trackMileage');
      if (!this.showTrackMileageInput) {
        control.reset();
      }
      control.updateValueAndValidity();
      this.formGroup.updateValueAndValidity();
    }
  }

  private async updateOdometerValueInputVisibility() {
    const wasVisible = this.showOdometerValueInput;

    if (this.isChangingProjects && this.openOdometerReading) {
      this.showOdometerValueInput = true; // Always show end odometer input if changing projects and there is an open odometer reading so that we can capture the end odometer reading
    } else if (this.formGroup.value.trackMileage) {
      this.showOdometerValueInput = true; // User answered yes to tracking mileage
    } else {
      this.showOdometerValueInput = false;
    }

    if (wasVisible !== this.showOdometerValueInput) {
      const control = this.formGroup.get('odometerValue');
      if (!this.showOdometerValueInput) {
        control.reset();
        this.clearOdometerPhoto(false);
      }
      control.updateValueAndValidity();        // re-run min/required/range
      this.formGroup.updateValueAndValidity(); // re-run overlap async validator
    }
  }

  private async validateOdometerReadingHasNoOverlap(control: AbstractControl): Promise<ValidationErrors | null> {
    if (!this.showOdometerValueInput || control.value == null) {
      return null;
    }
    const odometerReading = new OdometerReading({
      ...this.openOdometerReading,
      OdometerStart: this.openOdometerReading ? this.openOdometerReading.OdometerStart : control.value,
      OdometerEnd: this.openOdometerReading ? control.value : null,
      UserId: this.userId,
      Date: DateTimeHelper.formatDateTimeAsDate(this.date)
    });

    const overlaps = await this.odometerReadingService.getOverlappingOdometerReadings(odometerReading);
    return overlaps.length ? { overlap: true } : null;
  };

  private validateOdometerRange() {
    return (control: AbstractControl): ValidationErrors | null => {
      if (!this.showOdometerValueInput || !this.openOdometerReading || !control.value) {
        return null;
      }

      const odometerReading = new OdometerReading({
        OdometerStart: this.openOdometerReading.OdometerStart,
        OdometerEnd: control.value,
        UserId: this.userId,
        Date: DateTimeHelper.formatDateTimeAsDate(this.date)
      });

      const isValid = this.odometerReadingService.checkIsEndMilageGreaterOrEqualStartMilage(odometerReading);
      return isValid ? null : { range: true };
    };
  }

  getErrors(target: AbstractControl | FormGroup | null = null): string[] {
    const control = target || this.formGroup;
    if (!control?.errors) {
      return [];
    }

    return Object.keys(control.errors)
      .map(key => {
        // Don't show required error if control is not touched
        if (!control.touched && key === 'required') {
          return '';
        }

        switch (key) {
          case 'required':
            return 'This field is required';
          case 'min':
            return `Odometer reading must be greater than or equal to ${control.errors[key].min}`;
          case 'overlap':
            return 'Your odometer reading overlaps with another reading for today';
          case 'range':
            return 'Ending odometer reading must be greater than or equal to starting odometer reading';
          default:
            return 'Unknown error';
        }
      })
      .filter(error => error !== '');
  }

  get isChangingProjects(): boolean {
    return this.currentProjectId !== this.nextProjectId;
  }

  markAsTouched() {
    if (this.formGroup) {
      Object.values(this.formGroup.controls).forEach(async control => {
        await control.markAsTouched();
        await control.updateValueAndValidity();
      });
    }
  }
}
